import { Col, Row } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { INITIAL_PAGE } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import Form from "../../form-components/from";
import ReportContext from "../../../context/report/context";
import { getReportType, ReportType } from "../../../models/report/enum";
import EndPoints from "../../../services/end-points";
import { useWatch } from "react-hook-form";
import { createActiveNamlieahStoreFilters } from "../../../utils/helpers/store-filters";

interface IProps {}

const ProductMarginFilters = () => {
  const { t } = useTranslation();

  const [stores, setStores] = useState<any[]>();
  const [storesLoading, setStoresLoading] = useState(true);

  useEffect(() => {
    const getStores = async () => {
      try {
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: createActiveNamlieahStoreFilters(),
        });
        setStores(data?.data);
      } catch (error) {
      } finally {
        setStoresLoading(false);
      }
    };
    getStores();
  }, []);

  return (
    <>
      <Col span={8}>
        <FieldBuilder
          name="store_id"
          label={`${t("store")}`}
          width="large"
          rules={{ required: false }}
          input={{
            type: "select",
            loading: storesLoading,
            allowSearch: true,
            options: stores?.map((store, index) => {
              return {
                key: index,
                label: store?.name,
                value: store?.id,
              };
            }),
          }}
        />
      </Col>
      <Col span={8}>
        <FieldBuilder
          name="store_quantity_status"
          label={`${t("store_status")}`}
          width="large"
          rules={{ required: false }}
          input={{
            type: "select",
            options: [1, 2].map((status) => {
              return {
                label: t(status === 1 ? "active" : "inactive"),
                value: status,
              };
            }),
          }}
        />
      </Col>
    </>
  );
};

const ReportFilterContent = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(ReportContext);

  const typeWatch = useWatch({
    name: "type",
  });

  const isProductMargin = typeWatch === ReportType.PRODUCT_MARGIN_REPORT_RES;

  const type = [
    ReportType.PHARMACIES_ORDERS_RES,
    ReportType.ORDER_USER_DETAILS_RES,
    ReportType.ORDERS_CANCELLATION_RES,
    ReportType.DRIVERS_PERFORMANCE_RES,
    ReportType.ORDERS_DAYLE_INFO_RES,
    ReportType.ORDERS_STATUS_RES,
    ReportType.PAID_ORDERS_JOURNEY_RES,
    ReportType.ORDERS_DETAILS_RES,
    ReportType.USER_ACTIVITY_REPORT_RES,
    ReportType.MONTHLY_USER_ACTIVITY_REPORT_RES,
    ReportType.LOW_STOCK_REPORT_RES,
    ReportType.PRODUCT_MARGIN_REPORT_RES,
    ReportType.PRODUCT_BATCH_STOCK_REPORT_RES,
    ReportType.SECTOR_DELIVERY_TIME_REPORT_RES,
    ReportType.STORE_ACCEPTANCE_TIME_REPORT_RES,
    ReportType.PREPARING_TIME_REPORT_RES,
    ReportType.ORDER_CYCLE_TIME_REPORT_RES,
    ReportType.MODIFIED_ORDER_ACCEPTANCE_TIME_REPORT_RES,
    ReportType.ACCOUNTING_FINALIZATION_TIME_REPORT_RES,
    ReportType.DRIVER_SEARCH_TIME_REPORT_RES,
    ReportType.CUSTOMER_CANCELLATION_REPORT_RES,
    ReportType.ADMINISTRATIVE_CANCELLATION_REPORT_RES,
    ReportType.CUSTOMER_SATISFACTION_REPORT_RES,
    ReportType.COUPON_IMPACT_RES,
    ReportType.CART_BEHAVIOR_REPORT_RES,
    ReportType.CATEGORY_SALES_TIMING_REPORT_RES,
    ReportType.RFM_REPORT_RES,
  ];
  const statusOptions = [
    "pending",
    "processing",
    "available",
    "failed",
    "expired",
    "cancelled",
  ];

  return (
    <Row justify={"start"} gutter={[10, 0]}>
      <Col span={8}>
        <FieldBuilder
          name="type"
          label={`${t("type")}`}
          width="large"
          rules={{ required: false }}
          input={{
            type: "select",
            options: type.map((el) => {
              return {
                label: t(getReportType(el)),
                value: el,
              };
            }),
          }}
        />
      </Col>
      <Col span={8}>
        <FieldBuilder
          name="status"
          label={`${t("status")}`}
          width="large"
          rules={{ required: false }}
          input={{
            type: "select",
            options: statusOptions.map((status) => ({
              label: t(`report_status_${status}`),
              value: status,
            })),
          }}
        />
      </Col>
      {isProductMargin && <ProductMarginFilters />}
    </Row>
  );
};

const ReportFilter: React.FC<IProps> = () => {
  const { actions, query } = useContext(ReportContext);

  return (
    <Form
      formId="report-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: data?.keyword,
          filters: [
            data?.type
              ? {
                  name: "type",
                  value: data.type,
                  operation: "eq",
                }
              : undefined,
            data?.store_id
              ? {
                  name: "store_id",
                  value: data.store_id,
                  operation: "eq",
                }
              : undefined,
            data?.store_quantity_status
              ? {
                  name: "store_quantity_status",
                  value: data.store_quantity_status,
                  operation: "eq",
                }
              : undefined,
            data?.status
              ? {
                  name: "status",
                  value: data.status,
                  operation: "eq",
                }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <ReportFilterContent />
    </Form>
  );
};

export default ReportFilter;
